import type { Texture } from 'pixi.js';
import type { HeroAnimationFacing } from '@/modules/game-domain/render-snapshot';
import { tryResolveGameAssetFile } from '@/game/assets/resolveGameAssetUrl';
import { AnimationAtlasCache } from './animation-atlas-cache';
import { meshyNpcClip, type MeshyNpcModel, type MeshyNpcClip } from './meshy-npc-clips';

export interface MeshyNpcAtlasRequest { model: MeshyNpcModel; clip: MeshyNpcClip; facing: HeroAnimationFacing }
/** One shared bounded cache lease per actually demanded direction/clip; no all576 preload. */
export class MeshyNpcAssets extends AnimationAtlasCache {
  get(model: MeshyNpcModel, clip: MeshyNpcClip, facing: HeroAnimationFacing): readonly Texture[] | null {
    const definition = meshyNpcClip(model, clip, facing);
    const url = tryResolveGameAssetFile(definition.file);
    if (!url) return null;
    return this.requestAtlas({ url, width: definition.width, height: definition.height,
      columns: definition.columns, rows: definition.rows, scaleMode: 'nearest' });
  }
  async preload(requests: readonly MeshyNpcAtlasRequest[]): Promise<void> {
    // Sequential demand avoids reserving an entire town's possible idle variants at once.
    const seen = new Set<string>();
    for (const request of requests) {
      const key = `${request.model}:${request.clip}:${request.facing}`;
      if (seen.has(key)) continue;
      seen.add(key);
      this.get(request.model, request.clip, request.facing);
      await this.waitForLoads();
    }
  }
}
